import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import PujaCountdown from "./PujaCountdown";
import { useAuth } from "@/lib/auth";
import {
  House, Package, Database, ClipboardText,
  ChartBar, SignOut, Warehouse, ArrowsLeftRight,
  Receipt, Users, Barcode, ShieldCheck,
  NotePencil, List, X,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/app", label: "Dashboard", icon: House, perms: ["jobs:read", "inventory:read"] },
  { to: "/jobs", label: "Job Sheet", icon: ClipboardText, perms: ["jobs:read"] },
  { to: "/jobs/unresolved", label: "Unresolved Drafts", icon: NotePencil, perms: ["jobs:correct"] },
  { to: "/challans", label: "Challans", icon: Barcode, perms: ["challan:create", "challan:cancel"] },
  { to: "/purchases", label: "Purchases", icon: Receipt, perms: ["purchase:write"] },
  { to: "/transfers", label: "Transfers", icon: ArrowsLeftRight, perms: ["transfer:write"] },
  { to: "/stock", label: "Current Stock", icon: Warehouse, perms: ["inventory:read"] },
  { to: "/daily-report", label: "Daily Report", icon: ChartBar, perms: ["jobs:read"] },
  { to: "/ledger", label: "Client Ledger", icon: Users, perms: ["jobs:read"] },
  { to: "/masters", label: "Master Data", icon: Database, perms: ["masters:read", "masters:write"] },
  { to: "/opening-stock", label: "Opening Stock", icon: Package, perms: ["inventory:override"] },
  { to: "/audit", label: "Audit Log", icon: ShieldCheck, perms: ["audit:read"], roles: ["super_admin","admin"] },
  { to: "/employees", label: "Employee Master", icon: Users, perms: ["users:manage"], roles: ["super_admin","admin"] },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const showPujaCountdown = ["/app", "/jobs"].includes(pathname.replace(/\/+$/, ""));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const hasExplicitAccess = Array.isArray(user?.permissions);
  const items = NAV.filter(item => {
    if (user?.role === "super_admin") return true;
    if (hasExplicitAccess && item.perms) return item.perms.some(permission => user.permissions.includes(permission));
    return !item.roles || item.roles.includes(user?.role);
  });

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setMobileMenuOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    document.body.classList.toggle("employee-menu-open", mobileMenuOpen);
    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      document.body.classList.remove("employee-menu-open");
    };
  }, [mobileMenuOpen]);

  return (
    <div className="employee-app min-h-[100dvh] w-full bg-white flex overflow-x-hidden" data-testid="app-shell">
      <aside className={`employee-sidebar fixed md:sticky inset-y-0 left-0 top-0 z-50 w-[min(18rem,86vw)] md:w-60 border-r border-zinc-200 flex flex-col shrink-0 bg-white h-[100dvh] transition-transform duration-200 ${mobileMenuOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}>
        <div className="employee-brand px-4 py-4 border-b border-zinc-200">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="employee-brand-name font-heading font-black text-lg tracking-tight">
                <span>JYOTI</span> <span>IMAGE</span>
              </div>
              <div className="employee-cmyk-dots" aria-hidden="true">
                <span></span><span></span><span></span><span></span>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mt-0.5">CTP OPS SYSTEM</div>
            </div>
            <button type="button" className="md:hidden rounded-lg border border-zinc-200 p-2 text-zinc-700" onClick={()=>setMobileMenuOpen(false)} aria-label="Close navigation"><X size={17}/></button>
          </div>
        </div>
        <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto" data-testid="side-nav">
          {items.map(it => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/app"}
              onClick={()=>setMobileMenuOpen(false)}
              data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g,"-")}`}
              className={({isActive}) => `side-nav-item ${isActive ? "active" : ""}`}
            >
              <it.icon size={16} weight="duotone" />
              <span>{it.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="employee-user-panel border-t border-zinc-200 p-3">
          <div className="text-xs font-semibold text-zinc-900 truncate" data-testid="current-user-name">{user?.name || user?.employee_id || user?.email}</div>
          <div className="text-[10px] text-zinc-500 font-mono truncate">{user?.employee_id || user?.email}</div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mt-0.5">{user?.role}</div>
          <button
            data-testid="logout-btn"
            onClick={async () => { await logout(); nav("/login"); }}
            className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-zinc-300 hover:bg-zinc-100 text-zinc-900 text-xs font-semibold px-3 py-1.5 rounded-sm transition-colors"
          >
            <SignOut size={14} /> Sign out
          </button>
        </div>
      </aside>
      {mobileMenuOpen && <button type="button" className="fixed inset-0 z-40 bg-zinc-950/45 md:hidden" onClick={()=>setMobileMenuOpen(false)} aria-label="Close navigation overlay" />}
      <main className="employee-main flex-1 min-w-0 min-h-[100dvh] w-full max-w-full bg-zinc-50/40">
        <div className="employee-mobile-header md:hidden sticky top-0 z-30 flex items-center justify-between gap-3 px-4 py-3 bg-white border-b border-zinc-200">
          <button type="button" className="inline-flex items-center justify-center rounded-lg border border-zinc-200 p-2 text-zinc-800" onClick={()=>setMobileMenuOpen(true)} aria-label="Open navigation" aria-expanded={mobileMenuOpen}><List size={20}/></button>
          <div className="employee-brand-name font-heading font-black text-base tracking-tight"><span>JYOTI</span> <span>IMAGE</span></div>
          <div className="w-9" aria-hidden="true" />
        </div>
        {showPujaCountdown && <PujaCountdown />}
        <Outlet />
      </main>
    </div>
  );
}
